/**
 * ErpSyncTab — zakładka „Synchronizacja" ekranu Integracja ERP.
 *
 * Zbiera istniejące modale sync (kontrahenci / towary / dokumenty) w jednym miejscu,
 * pogrupowane per encja. Świadomie NIE przepisuje logiki sync — otwiera te same modale
 * co IntegrationsTab, z identycznym kontraktem propsów. Zmiana czysto frontendowa.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import { Tabs } from '@/components/Tabs';
import { api } from '@/lib/api';

import { ContractorSyncPanel } from './ContractorSyncPanel';
import { DictionarySyncPanel } from './DictionarySyncPanel';
import { DocumentSyncPanel } from './DocumentSyncPanel';
import { EmployeeSyncPanel } from './EmployeeSyncPanel';
import { ExportContractorsModal } from './ExportContractorsModal';
import { type ErpIntegrationDto } from './IntegrationsTab';
import { SyncContractorsModal } from './SyncContractorsModal';
import { SyncEmployeesModal } from './SyncEmployeesModal';
import { SyncProductsModal } from './SyncProductsModal';

type Entity = 'contractors' | 'products' | 'employees' | 'documents';
type SyncTarget = { id: string; name: string } | null;

function formatLastSync(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export function ErpSyncTab({ integrationId }: { integrationId?: string } = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [syncContractorsFor, setSyncContractorsFor] = useState<SyncTarget>(null);
  const [syncProductsFor, setSyncProductsFor] = useState<SyncTarget>(null);
  const [syncEmployeesFor, setSyncEmployeesFor] = useState<SyncTarget>(null);
  const [exportContractorsFor, setExportContractorsFor] = useState<SyncTarget>(null);

  const listQ = useQuery({
    queryKey: ['erp-integrations', 'sync'],
    queryFn: async () => {
      const res = await api.get<{ integrations: ErpIntegrationDto[] }>('/erp-integrations', {
        params: {},
      });
      return res.data.integrations;
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['erp-integrations'] });
  };

  if (listQ.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner size="lg" />
      </div>
    );
  }

  // Widok szczegółu (master-detail): zawężamy do wskazanej integracji; inaczej wszystkie aktywne.
  const integrations = (listQ.data ?? []).filter((it) =>
    integrationId ? it.id === integrationId : it.gcrecord == null,
  );

  if (integrations.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border-secondary p-10 text-center text-sm text-text-secondary">
        {t('tenant.settings.integrations.sync.empty')}
      </div>
    );
  }

  // Pracownicy są WYŁĄCZNIE po stronie FK (Handel pracowników nie ma) — zakładka i wiersze
  // ograniczone do integracji symfonia_fk.
  const fkIntegrations = integrations.filter((it) => it.erpSystem === 'symfonia_fk');
  const showEmployees = fkIntegrations.length > 0;

  function renderRows(
    entity: Entity,
    onSync: (target: { id: string; name: string }) => void,
    rows: ErpIntegrationDto[] = integrations,
  ) {
    return (
      <ul className="space-y-2">
        {rows.map((it) => (
          <li
            key={it.id}
            className="flex items-center justify-between gap-3 rounded-md border border-border-primary bg-bg-secondary px-4 py-3"
          >
            <div className="min-w-0">
              <div className="truncate font-medium text-text-primary">{it.name}</div>
              {it.lastSyncAt && (
                <div className="text-xs text-text-tertiary">
                  {t('tenant.settings.integrations.sync.lastSync')} {formatLastSync(it.lastSyncAt)}
                </div>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {/* Eksport MY → Symfonia — tylko kontrahenci i tylko FK (endpoint push-contractors
                  jest FK-only). Kierunek odwrotny do „Synchronizuj" (pull). */}
              {entity === 'contractors' && it.erpSystem === 'symfonia_fk' && (
                <Button
                  variant="secondary"
                  onClick={() => setExportContractorsFor({ id: it.id, name: it.name })}
                  data-testid={`erp-export-contractors-${it.id}`}
                >
                  {t('tenant.settings.integrations.sync.exportButton')}
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => onSync({ id: it.id, name: it.name })}
                data-testid={`erp-sync-${entity}-${it.id}`}
              >
                {t('tenant.settings.integrations.sync.syncButton')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  // Widok szczegółu (jedna integracja) → bogaty pulpit kontrahentów (wzór SellCall).
  const singleIntegration = integrationId ? (integrations[0] ?? null) : null;

  // FK (księgowość) nie ma towarów — produkty/magazyn to pojęcie Handla. Zakładkę „Produkty"
  // pokazujemy tylko dla integracji, które faktycznie synchronizują towary (nie-FK).
  const isFk = singleIntegration?.erpSystem === 'symfonia_fk';

  const syncTabs = [
    {
      value: 'contractors',
      label: t('tenant.settings.integrations.sync.entityTabs.contractors'),
      content: singleIntegration ? (
        <ContractorSyncPanel integration={singleIntegration} />
      ) : (
        renderRows('contractors', setSyncContractorsFor)
      ),
    },
    // Handel ma towary (produkty), FK ma pracowników. Produkty pokazujemy tylko poza FK;
    // pracownicy FK dojdą w osobnej sesji (pełny sync UI + backend).
    ...(isFk
      ? []
      : [
          {
            value: 'products',
            label: t('tenant.settings.integrations.sync.entityTabs.products'),
            content: renderRows('products', setSyncProductsFor),
          },
        ]),
    // Pracownicy — FK-only (Handel pracowników nie udostępnia); zakładka tylko gdy jest
    // integracja Symfonia FK, wiersze zawężone do FK.
    ...(showEmployees
      ? [
          {
            value: 'employees',
            label: t('tenant.settings.integrations.sync.entityTabs.employees'),
            // Tryb detalu (jedna integracja FK) → bogaty pulpit pracowników (Import/Eksport/
            // Automatyzacja + wykluczenia), jak przy kontrahentach. Widok listy → proste wiersze.
            content:
              singleIntegration && isFk ? (
                <EmployeeSyncPanel integration={singleIntegration} />
              ) : (
                renderRows('employees', setSyncEmployeesFor, fkIntegrations)
              ),
          },
        ]
      : []),
    {
      value: 'documents',
      label: t('tenant.settings.integrations.sync.entityTabs.documents'),
      // Dokumenty = TYLKO EKSPORT (my → FK). Tryb detalu → pulpit informacyjny (bez importu).
      // Widok listy → nota o kierunku (brak wiersza „Synchronizuj”, bo importu dokumentów nie ma).
      content: singleIntegration ? (
        <DocumentSyncPanel integration={singleIntegration} />
      ) : (
        <p className="rounded-md border border-dashed border-border-secondary px-4 py-6 text-center text-xs text-text-secondary">
          {t('tenant.settings.integrations.sync.documentsExportOnlyNote')}
        </p>
      ),
    },
    // Słowniki — dane referencyjne (Symfonia → my, jednokierunkowo). W trybie detalu jako panel;
    // dotyczy obu systemów (Handel i FK mają własne słowniki).
    ...(singleIntegration
      ? [
          {
            value: 'dictionaries',
            label: t('tenant.settings.integrations.sync.entityTabs.dictionaries'),
            content: <DictionarySyncPanel integration={singleIntegration} />,
          },
        ]
      : []),
  ];

  return (
    <div>
      <Tabs idPrefix="erp-sync" items={syncTabs} />

      <SyncContractorsModal
        open={syncContractorsFor !== null}
        integrationId={syncContractorsFor?.id ?? null}
        integrationName={syncContractorsFor?.name ?? ''}
        onClose={() => {
          setSyncContractorsFor(null);
          invalidate();
        }}
      />
      <SyncProductsModal
        open={syncProductsFor !== null}
        integrationId={syncProductsFor?.id ?? null}
        integrationName={syncProductsFor?.name ?? ''}
        onClose={() => {
          setSyncProductsFor(null);
          invalidate();
        }}
      />
      <SyncEmployeesModal
        open={syncEmployeesFor !== null}
        integrationId={syncEmployeesFor?.id ?? null}
        integrationName={syncEmployeesFor?.name ?? ''}
        onClose={() => {
          setSyncEmployeesFor(null);
          invalidate();
        }}
      />
      <ExportContractorsModal
        open={exportContractorsFor !== null}
        integrationId={exportContractorsFor?.id ?? null}
        integrationName={exportContractorsFor?.name ?? ''}
        onClose={() => {
          setExportContractorsFor(null);
          invalidate();
        }}
      />
    </div>
  );
}
